// Phase 2.4 — REST-backed `studentService` (the Firestore `students` collection).
//
// Mirrors the legacy studentService surface:
//   createStudent, getStudents([classId]), findByFatherPhone(phone),
//   getStudentById, updateStudent, archiveStudent, unarchiveStudent,
//   getArchivedStudents, permanentlyDeleteStudent.
//
// `permanentlyDeleteStudent` calls DELETE /api/students/:id on the backend,
// which only removes the student row. The legacy fan-out across dailyLogs/
// sessions/attendance/etc. moves server-side as those collections come
// online (Phase 2.5+); that side-effect graph is documented in the route
// handler for future extension.

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4001';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

function payload(studentData) {
  // Backend expects ISO strings for date fields; the form passes Date /
  // dayjs objects in places.
  const toIso = (v) => {
    if (!v) return v;
    if (typeof v === 'string') return v;
    if (typeof v?.toISOString === 'function') return v.toISOString();
    if (typeof v?.toDate === 'function') return v.toDate().toISOString();
    return v;
  };
  const out = { ...studentData };
  if ('dateOfBirth' in out) out.dateOfBirth = toIso(out.dateOfBirth) ?? null;
  if ('enrollmentDate' in out) out.enrollmentDate = toIso(out.enrollmentDate) ?? null;
  return out;
}

export const studentService = {
  async createStudent(studentData) {
    const memorizedPortions = studentData.memorizedPortions || [];
    const body = payload({
      ...studentData,
      studentFullName: studentData.studentFullName
        || `${studentData.studentFirstName ?? ''} ${studentData.studentLastName ?? ''}`.trim()
        || 'بدون اسم',
      memorizedPortions,
      priorMemorizedPortions: studentData.priorMemorizedPortions ?? [...memorizedPortions],
      autoMemorizedPortions: studentData.autoMemorizedPortions ?? [],
      isActive: true
    });
    const { student } = await apiFetch('/api/students', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return student.id;
  },

  async getStudents(classId = null) {
    try {
      const q = classId ? `?classId=${encodeURIComponent(classId)}` : '';
      const { students } = await apiFetch(`/api/students${q}`);
      return students;
    } catch (err) {
      if (err.status === 403) {
        console.warn('studentServiceV2.getStudents: no tenant context, returning empty list');
        return [];
      }
      throw err;
    }
  },

  async findByFatherPhone(phone) {
    if (!phone) return { tenantId: null, studentIds: [], students: [] };
    try {
      const { students } = await apiFetch(`/api/students?fatherPhone=${encodeURIComponent(phone)}`);
      if (!students.length) return { tenantId: null, studentIds: [], students: [] };
      const tenantId = students[0].tenantId;
      const sameTenant = students.filter((r) => r.tenantId === tenantId);
      return { tenantId, studentIds: sameTenant.map((r) => r.id), students: sameTenant };
    } catch (err) {
      console.warn('studentServiceV2.findByFatherPhone failed:', err.message);
      return { tenantId: null, studentIds: [], students: [] };
    }
  },

  async getStudentById(studentId) {
    if (!studentId) throw new Error('Error fetching student: studentId required');
    const { student } = await apiFetch(`/api/students/${encodeURIComponent(studentId)}`);
    return student;
  },

  async updateStudent(studentId, studentData) {
    if (!studentId) throw new Error('Error updating student: studentId required');
    await apiFetch(`/api/students/${encodeURIComponent(studentId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload(studentData))
    });
  },

  async archiveStudent(studentId) {
    await apiFetch(`/api/students/${encodeURIComponent(studentId)}/archive`, { method: 'POST' });
  },

  async unarchiveStudent(studentId) {
    await apiFetch(`/api/students/${encodeURIComponent(studentId)}/unarchive`, { method: 'POST' });
  },

  async getArchivedStudents() {
    try {
      const { students } = await apiFetch('/api/students?isActive=false');
      return students.sort((a, b) => {
        const at = a.archivedAt ? new Date(a.archivedAt).getTime() : 0;
        const bt = b.archivedAt ? new Date(b.archivedAt).getTime() : 0;
        return bt - at;
      });
    } catch (err) {
      if (err.status === 403) {
        console.warn('studentServiceV2.getArchivedStudents: no tenant context, returning empty list');
        return [];
      }
      throw err;
    }
  },

  async permanentlyDeleteStudent(studentId) {
    if (!studentId) throw new Error('studentId is required');
    await apiFetch(`/api/students/${encodeURIComponent(studentId)}`, { method: 'DELETE' });
    // Phase 2.5+ will fan out the related-collection cleanup on the server
    // when those collections come online; the response here only reports
    // the student-row delete.
  }
};

export default studentService;
